import { useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useVerifyQueries } from "@/api/runs";
import type { VerifyQuery } from "@/api/types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  runId: string;
}

const LANGUAGE_LABEL: Record<string, string> = {
  logql: "LogQL",
  promql: "PromQL",
  traceql: "TraceQL",
  kql: "KQL",
  "datadog-logs": "Logs",
  "datadog-metrics": "Metrics",
  "datadog-apm": "APM",
  dql: "DQL",
  "cw-insights": "CW Insights",
  "xray-filter": "X-Ray filter",
  text: "Reference",
};

export function VerifyQueriesPanel({ runId }: Props) {
  const queries = useVerifyQueries(runId);

  if (queries.isLoading) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted-foreground">Loading validation queries…</p>
      </Card>
    );
  }

  if (queries.isError || !queries.data) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted-foreground">
          Couldn't load validation queries.
        </p>
      </Card>
    );
  }

  if (queries.data.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted-foreground">
          No validation queries for this backend kind yet.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <header>
        <h3 className="text-sm font-semibold text-foreground">Validation queries</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Paste these into the backend's UI to prove the synthetic data landed.
        </p>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {queries.data.map((q) => (
          <QueryCard key={q.id} query={q} />
        ))}
      </div>
    </div>
  );
}

function QueryCard({ query }: { query: VerifyQuery }) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(query.query);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // older browsers / insecure contexts — fall back to selection prompt
      const ok = window.prompt("Copy:", query.query);
      if (ok !== null) {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-foreground">{query.name}</h4>
            <span className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-mono uppercase tracking-wide bg-muted text-muted-foreground border border-border">
              {LANGUAGE_LABEL[query.language] ?? query.language}
            </span>
          </div>
          {query.description && (
            <p className="text-xs text-muted-foreground mt-1">{query.description}</p>
          )}
        </div>
      </div>

      <pre className="text-xs font-mono bg-muted/50 border border-border rounded-md p-2 overflow-x-auto whitespace-pre text-foreground">
        {query.query}
      </pre>

      <div className="flex items-center justify-end gap-2">
        {query.deep_link_url && (
          <a
            href={query.deep_link_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Open in backend <ExternalLink size={11} />
          </a>
        )}
        <button
          type="button"
          onClick={onCopy}
          className={cn(
            "inline-flex items-center gap-1 text-xs px-2 h-7 rounded-md border transition-colors",
            copied
              ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-300"
              : "border-border text-foreground hover:bg-muted",
          )}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
